<template>
  <el-dialog
    v-model="visible"
    title="更换硬件"
    width="520px"
    :close-on-click-modal="false"
    class="replace-dialog"
    @close="handleClose"
  >
    <div class="dialog-hero">
      <div class="hero-icon-wrap">
        <el-icon :size="22" color="#fff"><RefreshRight /></el-icon>
      </div>
      <div class="hero-text">
        <h3>更换终端硬件</h3>
        <p>设备板子损坏时更换，原 endpointId 和绑船关系保持不变</p>
      </div>
    </div>

    <!-- 当前信息 -->
    <div class="current-info">
      <div class="info-label">当前终端编号</div>
      <div class="info-value">{{ currentDeviceId || "(无)" }}</div>
      <div class="info-label" style="margin-top: 12px">当前终端名称</div>
      <div class="info-value">{{ currentName || "(无)" }}</div>
    </div>

    <el-form label-position="top" class="replace-form">
      <el-form-item label="新终端编号 (deviceId)" required>
        <el-input
          v-model="newDeviceId"
          placeholder="如 IMEI860123045678902"
          clearable
        />
      </el-form-item>
      <el-form-item label="新终端名称">
        <el-input
          v-model="newName"
          placeholder="留空则不修改原名称"
          clearable
        />
      </el-form-item>
      <el-form-item label="备注">
        <el-input
          v-model="remark"
          type="textarea"
          :rows="2"
          placeholder="可选，如：板子进水损坏"
        />
      </el-form-item>
    </el-form>

    <el-alert type="warning" :closable="false" class="warn-alert">
      <template #title>
        <div class="warn-text">
          · 旧终端将被踢线，无法再连接<br />
          · 新终端需烧录证书，连接后自动上线<br />
          · 设备绑船关系和历史数据保持不变<br />
          · 新终端上线后，请点击【迁移设备】恢复设备数据采集<br />
          <span class="warn-sub">（船舶运营协议出厂已预置，无需迁移）</span>
        </div>
      </template>
    </el-alert>

    <template #footer>
      <el-button size="large" @click="visible = false">取消</el-button>
      <el-button
        type="primary"
        size="large"
        :loading="submitting"
        class="submit-btn"
        @click="handleSubmit"
      >
        <el-icon><Check /></el-icon>
        <span>确认更换</span>
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { RefreshRight, Check } from "@element-plus/icons-vue";
import { replaceHardwareApi } from "@/api/cert/certBatchApi";

const props = defineProps<{
  modelValue: boolean;
  endpointId: string;
  currentDeviceId: string;
  currentName?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  success: [];
}>();

const visible = ref(props.modelValue);
const newDeviceId = ref("");
const newName = ref("");
const remark = ref("");
const submitting = ref(false);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      newDeviceId.value = "";
      // 名称默认填入原名称，留空则不修改
      newName.value = props.currentName || "";
      remark.value = "";
    }
  }
);
watch(visible, (val) => emit("update:modelValue", val));

const handleClose = () => {
  newDeviceId.value = "";
  newName.value = "";
  remark.value = "";
};

const handleSubmit = async () => {
  if (!newDeviceId.value.trim()) {
    ElMessage.warning("请输入新终端编号");
    return;
  }

  submitting.value = true;
  try {
    // 名称留空时不传该字段，服务端只更新非空字段
    await replaceHardwareApi(
      props.endpointId,
      newDeviceId.value.trim(),
      // 仅在填了名称时传，名称为空 = 不修改
      newName.value.trim() || undefined,
      remark.value.trim() || undefined
    );
    ElMessage.success(
      "硬件更换成功，新终端上线后请点击【迁移设备】恢复设备数据采集"
    );
    emit("success");
    visible.value = false;
  } catch (e: any) {
    ElMessage.error(e.message || "更换失败");
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
:deep(.replace-dialog .el-dialog__header) {
  padding: 20px 24px 0;
}
:deep(.replace-dialog .el-dialog__body) {
  padding: 16px 24px;
}
:deep(.replace-dialog .el-dialog__footer) {
  padding: 12px 24px 20px;
  border-top: 1px solid #f1f5f9;
}

/* 顶部英雄区 */
.dialog-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  margin-bottom: 20px;
}
.hero-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}
.hero-text h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* 当前信息卡片 */
.current-info {
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
  margin-bottom: 16px;
}
.info-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}
.info-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
  font-family: "SF Mono", Monaco, monospace;
}

/* 表单 */
:deep(.replace-form .el-form-item__label) {
  font-weight: 600;
  color: #475569;
  padding-bottom: 6px;
}
:deep(.replace-form .el-input__wrapper),
:deep(.replace-form .el-textarea__inner) {
  border-radius: 8px;
}

/* 警告 */
.warn-alert {
  margin-top: 16px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}
:deep(.warn-alert .el-alert__title) {
  color: #92400e;
}
.warn-text {
  font-size: 13px;
  line-height: 1.8;
  color: #92400e;
}
.warn-sub {
  font-size: 12px;
  color: #b45309;
  opacity: 0.85;
}

/* 提交按钮 */
.submit-btn {
  border-radius: 10px !important;
  padding: 0 28px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg, #3b82f6, #6366f1) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}
.submit-btn:hover {
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.4) !important;
}
</style>
